import {useEffect} from 'react';
import {useEvent, useTimeoutRef} from '@chekinapp/ui';
import {StatusModalStates} from '../../../components';
import type {ProgressPhase} from '../components/flow/PaymentProgress';
import {usePaymentStageStore} from './usePaymentStageStore';

const RECEIPTS_DELAY_MS = 2100;

const PHASE: Partial<Record<StatusModalStates, ProgressPhase>> = {
  [StatusModalStates.LOADING]: 'processing',
  [StatusModalStates.SUCCESS]: 'success',
  [StatusModalStates.ERROR]: 'error',
};

type Props = {
  processState: StatusModalStates;
  hasSuccessActions: boolean;
  onReceiptsReady?: () => void;
};

function usePaymentFlowPhase({processState, hasSuccessActions, onReceiptsReady}: Props) {
  const phase = PHASE[processState];
  const receiptsTimeoutRef = useTimeoutRef();

  const setPaymentStage = usePaymentStageStore(state => state.setStage);
  useEffect(() => {
    setPaymentStage(processState);
    return () => setPaymentStage(StatusModalStates.IDLE);
  }, [processState, setPaymentStage]);

  const handleReceiptsReady = useEvent(onReceiptsReady);
  useEffect(() => {
    if (phase !== 'success' || hasSuccessActions) return;
    receiptsTimeoutRef.current = setTimeout(handleReceiptsReady, RECEIPTS_DELAY_MS);
  }, [handleReceiptsReady, hasSuccessActions, phase, receiptsTimeoutRef]);

  return {
    phase,
    isPaying: Boolean(phase),
  };
}

export {usePaymentFlowPhase};
